import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DollarSign, TrendingUp, Clock, Loader2 } from 'lucide-react';
import { LabelList, Pie, PieChart } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { usePartnerCommissions, useCurrentMonthCommission, useIndividualTransactions } from '@/hooks/usePartnerCommissions';
import { usePartnerAnalytics } from '@/hooks/usePartnerAnalytics';

interface CommissionTrackerProps {
  partner: any;
}

function CommissionTracker({ partner }: CommissionTrackerProps) {
  const partnerType = partner.user_type || partner.partner_type;
  const isWhiteLabel = partnerType === 'white_label';

  // Get colors for charts
  const primaryColor =  partner.branding_settings?.colors?.primary;
  const secondaryColor = partner.branding_settings?.colors?.accent;
  // Removed debug and manual generation tools - commissions are calculated automatically

  // Use real data from hooks
  const { data: commissionsData, isLoading: commissionsLoading, error: commissionsError } = usePartnerCommissions();
  const { isLoading: currentMonthLoading, error: currentMonthError } = useCurrentMonthCommission();
  const { data: analytics, isLoading: analyticsLoading, error: analyticsError } = usePartnerAnalytics();
    const { data: transactionsData, isLoading: transactionsLoading, error: transactionsError } = useIndividualTransactions(20);

  // Commission records are now automatically generated and managed by the system

  const monthlyHistory = commissionsData?.commissions?.map(commission => ({
    month: new Date(commission.period_start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    revenue: commission.total_revenue,
    commission: commission.commission_amount,
    rate: commission.commission_rate,
    status: commission.status,
    netPayout: commission.net_payout,
    stripeFees: commission.stripe_fees,
    activeUsers: analytics?.users.active || 0 // Note: This should ideally come from historical data per month
  })) || [];

  // Fallback: if latest monthly record is not available yet (mid-month),
  // derive a live breakdown from the visible transactions list
  const derivedFromTransactions = (() => {
    const txs = transactionsData?.transactions || [];
    const subs = txs
      .filter(t => t.type === 'Subscription')
      .reduce((s, t) => s + (t.amount || 0), 0);
    const packs = txs
      .filter(t => t.type === 'Boost Pack')
      .reduce((s, t) => s + (t.amount || 0), 0);
    return { subs, packs, total: subs + packs };
  })();

  // Process real data (use latest commission record from overview)
  const latestCommission = commissionsData?.commissions?.[0];

  // Smart fallback: Use live transaction data if no commission records exist
  const hasCommissionData = latestCommission && latestCommission.total_revenue > 0;
  const liveData = derivedFromTransactions;

  const currentMonth = {
    totalRevenue: hasCommissionData ? latestCommission.total_revenue : liveData.total,
    baseRevenue: hasCommissionData ? latestCommission.subscription_revenue : liveData.subs,
    boostPackRevenue: hasCommissionData ? latestCommission.boost_pack_revenue : liveData.packs,
    commissionRate: latestCommission?.commission_rate || (isWhiteLabel ? 0 : 30),
    commissionAmount: hasCommissionData ? latestCommission.commission_amount :
      (isWhiteLabel ? liveData.total : liveData.total * 0.30),
    // For white-label partners, markup calculation
    markupAmount: isWhiteLabel
      ? (hasCommissionData ? latestCommission.commission_amount :
          // Live calculation: Total revenue minus estimated base costs
          Math.max(liveData.total - (liveData.total * 0.19 * 2 * 2), 0))
      : (hasCommissionData ? latestCommission.commission_amount : liveData.total * 0.30),
    // Fall back to analytics active users for display
    activeUsers: analytics?.users?.active || 0,
    // Add flag to show data source
    isLiveData: !hasCommissionData
  };
  // Show loading state
  if (commissionsLoading || currentMonthLoading || analyticsLoading || transactionsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading commission data...</span>
      </div>
    );
  }
  
  // Show error state
  if (commissionsError || currentMonthError || analyticsError || transactionsError) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Error Loading Commission Data</CardTitle>
            <CardDescription>
              {commissionsError?.message || currentMonthError?.message || analyticsError?.message || transactionsError?.message || 'Failed to load commission data'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  


  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800">Processing</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Month Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isWhiteLabel ? (
          <>
            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Current month ({new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})
                </p>
              </CardHeader>
              <CardFooter className="mt-auto pt-2 pb-2">
                <div className="text-2xl font-bold">${currentMonth.totalRevenue.toLocaleString()}</div>
              </CardFooter>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Active Customers</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Paying customers this month
                </p>
              </CardHeader>
              <CardFooter className="mt-auto pt-2 pb-2">
                <div className="text-2xl font-bold text-blue-600">
                  {currentMonth.activeUsers || (transactionsData?.transactions?.length ?
                    new Set(transactionsData.transactions.map(t => t.customer_email)).size : 0)}
                </div>
              </CardFooter>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Avg Revenue Per Customer</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Per customer this month
                </p>
              </CardHeader>
              <CardFooter className="mt-auto pt-2 pb-2">
                <div className="text-2xl font-bold text-green-600">
                  ${(() => {
                    const customerCount = currentMonth.activeUsers ||
                      (transactionsData?.transactions?.length ?
                        new Set(transactionsData.transactions.map(t => t.customer_email)).size : 0);
                    return customerCount > 0 ? Math.round(currentMonth.totalRevenue / customerCount) : 0;
                  })()}
                </div>
              </CardFooter>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Your Profit</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Revenue minus platform costs
                </p>
              </CardHeader>
              <CardFooter className="mt-auto pt-2 pb-2">
                <div className="text-2xl font-bold text-green-600">
                  ${currentMonth.markupAmount.toLocaleString()}
                </div>
              </CardFooter>
            </Card>
          </>
        ) : (
          <>
            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Current month ({new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})
                </p>
              </CardHeader>
              <CardFooter className="mt-auto pt-2 pb-2">
                <div className="text-2xl font-bold">${currentMonth.totalRevenue.toLocaleString()}</div>
              </CardFooter>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Commission Rate</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {currentMonth.activeUsers >= 10 ? 'Bonus rate active!' : `${10 - currentMonth.activeUsers} users to 35%`}
                </p>
              </CardHeader>
              <CardFooter className="mt-auto pt-2 pb-2">
                <div className="text-2xl font-bold">{currentMonth.commissionRate}%</div>
              </CardFooter>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Commission Earned</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  From ${currentMonth.totalRevenue.toLocaleString()} revenue
                </p>
              </CardHeader>
              <CardFooter className="mt-auto pt-2 pb-2">
                <div className="text-2xl font-bold text-green-600">${currentMonth.commissionAmount.toLocaleString()}</div>
              </CardFooter>
            </Card>

<Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Latest Payout</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  ${currentMonth.commissionAmount.toLocaleString()}
                </p>
              </CardHeader>
              <CardFooter className="mt-auto pt-2 pb-2">
                <div className={`text-2xl font-bold ${
                  monthlyHistory.length > 0 && monthlyHistory[0].status === 'paid' 
                    ? 'text-green-600' 
                    : monthlyHistory.length > 0 && monthlyHistory[0].status === 'processing'
                    ? 'text-blue-600'
                    : 'text-yellow-600'
                }`}>
                  {monthlyHistory.length > 0 ? monthlyHistory[0].status.charAt(0).toUpperCase() + monthlyHistory[0].status.slice(1) : 'Pending'}
                </div>
              </CardFooter>
            </Card>
          </>
        )}
      </div>

      <div className="space-y-4">
          {/* Revenue Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue Breakdown</CardTitle>
              <CardDescription>Source of your {isWhiteLabel ? 'revenue' : 'commission'}</CardDescription>
            </CardHeader>
            <CardContent>
              {derivedFromTransactions.total > 0 ? (
                <ChartContainer
                  config={{
                    amount: {
                      label: "Amount",
                    },
                    subscriptions: {
                      label: "Subscription Plans",
                      color: primaryColor,
                    },
                    boostpacks: {
                      label: "Boost Packs",
                      color: secondaryColor,
                    },
                  } satisfies ChartConfig}
                  className="mx-auto aspect-square max-h-[250px]"
                >
                  <PieChart>
                    <ChartTooltip
                      content={<ChartTooltipContent nameKey="amount" hideLabel />}
                    />
                    <Pie
                      data={[
                        {
                          source: "subscriptions",
                          amount: derivedFromTransactions.subs,
                          fill: primaryColor
                        },
                        {
                          source: "boostpacks",
                          amount: derivedFromTransactions.packs,
                          fill: secondaryColor
                        },
                      ]}
                      dataKey="amount"
                    >
                      <LabelList
                        dataKey="source"
                        className="fill-background"
                        stroke="none"
                        fontSize={12}
                        formatter={(value: string) =>
                          value === "subscriptions" ? "Subscriptions" : "Boost Packs"
                        }
                      />
                    </Pie>
                  </PieChart>
                </ChartContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                  No revenue data available
                </div>
              )}

              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: primaryColor
                      }}
                    ></div>
                    <span className="text-sm">Subscription Plans</span>
                  </div>
                  <span className="text-sm font-medium">${derivedFromTransactions.subs.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: secondaryColor
                      }}
                    ></div>
                    <span className="text-sm">Boost Packs</span>
                  </div>
                  <span className="text-sm font-medium">${derivedFromTransactions.packs.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Individual {isWhiteLabel ? 'Revenue' : 'Commission'} Transactions</CardTitle>
              <CardDescription>
                Detailed breakdown of each {isWhiteLabel ? 'revenue' : 'commission'} transaction
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>{isWhiteLabel ? 'Your Revenue' : 'Commission'}</TableHead>
                    {isWhiteLabel && (
                      <TableHead>Markup</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!transactionsData?.transactions || transactionsData.transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isWhiteLabel ? 6 : 5} className="text-center text-muted-foreground py-8">
                        No transactions found yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactionsData.transactions.map((transaction) => {
                      // Calculate commission/revenue for white-label vs co-branded
                      const commissionAmount = isWhiteLabel 
                        ? transaction.amount // White-label keeps full markup
                        : transaction.amount * (currentMonth.commissionRate / 100); // Co-branded gets percentage
                      
                      // UI-only per-transaction markup for white-label partners
                      const BASE_COST_PER_MINUTE = 0.19; // platform wholesale
                      const AVG_CALL_DURATION_MIN = 2;   // assumption used in monthly calc
                      const EST_CALLS_PER_DOLLAR = 2;    // align with monthly heuristic
                      let markupForTransaction = commissionAmount;
                      if (isWhiteLabel) {
                        if (transaction.type === 'Boost Pack') {
                          markupForTransaction = transaction.amount;
                        } else if (transaction.type === 'Subscription') {
                          const estimatedBaseCost = transaction.amount * EST_CALLS_PER_DOLLAR * AVG_CALL_DURATION_MIN * BASE_COST_PER_MINUTE;
                          markupForTransaction = Math.max(transaction.amount - estimatedBaseCost, 0);
                        }
                      }
                      
                      return (
                        <TableRow key={transaction.id}>
                          <TableCell className="font-medium">
                            {new Date(transaction.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              {transaction.customer_name && transaction.customer_name !== 'Unknown' && <span className="font-medium">{transaction.customer_name}</span>}
                              <span className="text-xs text-muted-foreground">{transaction.customer_email}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              style={{
                                backgroundColor:
                                  transaction.type === "Subscription" ? primaryColor : secondaryColor,
                              }}
                            >
                              {transaction.type}
                            </Badge>

                          </TableCell>
                          <TableCell>${transaction.amount.toFixed(2)}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            ${commissionAmount.toFixed(2)}
                          </TableCell>
                          {isWhiteLabel && (
                            <TableCell className="text-sm text-muted-foreground">
                              ${markupForTransaction.toFixed(2)}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Separator */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Monthly Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Summary</CardTitle>
              <CardDescription>
                Aggregated {isWhiteLabel ? 'revenue' : 'commission'} by month
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Total Revenue</TableHead>
                    <TableHead>{isWhiteLabel ? 'Your Markup' : 'Commission'}</TableHead>
                    <TableHead>Payout Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Show current month live data if no commission records exist but we have transaction data */}
                  {monthlyHistory.length === 0 && currentMonth.totalRevenue > 0 ? (
                    <TableRow>
                      <TableCell className="font-medium">
                        {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        <span className="text-xs text-blue-600 ml-1">• Live</span>
                      </TableCell>
                      <TableCell>${currentMonth.totalRevenue.toLocaleString()}</TableCell>
                      <TableCell className="font-semibold text-green-600">
                        ${(isWhiteLabel ? currentMonth.markupAmount : currentMonth.commissionAmount).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-blue-100 text-blue-800">🔄 Current Month</Badge>
                      </TableCell>
                    </TableRow>
                  ) : monthlyHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No monthly data available yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    monthlyHistory.map((month, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{month.month}</TableCell>
                      <TableCell>${month.revenue.toLocaleString()}</TableCell>
                      <TableCell className="font-semibold text-green-600">
                        ${month.commission.toLocaleString()}
                      </TableCell>
                      <TableCell>{getStatusBadge(month.status)}</TableCell>
                    </TableRow>
                  )))}
                </TableBody>
              </Table>
              
              {/* Total to be paid */}
              {monthlyHistory.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Total Pending Payout:</span>
                    <span className="text-lg font-bold text-green-600">
                      ${monthlyHistory
                        .filter(m => m.status === 'pending' || m.status === 'estimated')
                        .reduce((sum, m) => sum + m.commission, 0)
                        .toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500">
                      {monthlyHistory.filter(m => m.status === 'pending').length} pending, {monthlyHistory.filter(m => m.status === 'estimated').length} estimated month(s)
                    </span>
                    <span className="text-xs text-gray-500">
                      Fees: -${monthlyHistory
                        .filter(m => m.status === 'pending' || m.status === 'estimated')
                        .reduce((sum, m) => sum + (m.stripeFees || 0), 0)
                        .toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>


          {/* Commission Management Section */}
          {/* <Card>
            <CardContent>
              <div className="border-t pt-4 mt-6">
                  <h4 className="text-sm font-medium mb-3">Commission Management</h4>

                  {/* Commission records are automatically calculated and managed */}

                  {/* Process Payout */}
                  {/* <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-medium">Process Payout</h5>
                    <Button
                      onClick={handleTestPayout}
                      disabled={testingPayout}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      {testingPayout ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="h-4 w-4" />
                      )}
                      {testingPayout ? 'Testing...' : 'Test Payout (Safe)'}
                    </Button>
                  </div> */}

                      {/* {testResult && (
                    <div className={`p-3 rounded-md text-sm ${
                      testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                    }`}>
                      <div className="font-medium mb-1">
                        {testResult.success ? '✅ Test Successful' : '❌ Test Failed'}
                      </div>
                      <div className="text-xs">
                        Status: {testResult.status} |
                        {testResult.success ? ` Processed: ${testResult.data?.processed || 0} payouts` : ` Error: ${testResult.error || testResult.data?.error}`}
                      </div>
                          {testResult.success && (
                            <div className="mt-1 text-xs">
                              {(() => {
                                const processed = Number(testResult.data?.processed || 0);
                                const amount = processed === 0
                                  ? totalPendingPayout
                                  : (typeof testResult.data?.total_amount === 'number' ? testResult.data.total_amount : totalPendingPayout);
                                const label = 'Amount: $' + amount.toLocaleString();
                                const mode = (testResult.data?.dry_run !== false) ? 'Dry Run: No real money transferred' : 'REAL TRANSFER: Money sent to Stripe!';
                                return `${label} | ${mode}`;
                              })()}
                            </div>
                          )}
                    </div>
                  )} */}
                {/* </div>
            </CardContent>
          </Card> */}
        </div>
    </div>
  );
}

export default CommissionTracker;
